import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { upsertBrandOpsAutomation } from '../../../redux/brandOps/actions';
import { brandOpsMutatingAutomationSelector } from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import type { IBrandOpsAutomationRule, IBrandOpsCalendarItem } from '../../../redux/brandOps/types';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER } from '../brandDashboardTokens';
import { ManageGoldButton } from './manageShared';

interface Props {
    brandId: string;
    open: boolean;
    onClose: () => void;
    /** When editing an existing rule */
    existing?: IBrandOpsAutomationRule | null;
    /** Published IG calendar items to pick from when creating a new rule */
    igPosts: IBrandOpsCalendarItem[];
}

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
        '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
    },
    '& .MuiInputLabel-root': { color: BRAND_LUXE_MUTED },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_LUXE_PRIMARY },
};

export default function BrandManageAutomationRuleModal({ brandId, open, onClose, existing, igPosts }: Props) {
    const dispatchP = useDispatchPromise();
    const saving = useSelector(brandOpsMutatingAutomationSelector);

    const [selectedPostId, setSelectedPostId] = useState('');
    const [triggerWord, setTriggerWord] = useState('');
    const [dmMessage, setDmMessage] = useState('');
    const [publicReply, setPublicReply] = useState('Thanks! Check your DMs.');
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        if (open) {
            if (existing) {
                setSelectedPostId(existing.social_post_id);
                setTriggerWord(existing.automation.trigger_word || '');
                setDmMessage(existing.automation.dm_message || '');
                setPublicReply(existing.automation.public_reply_text || 'Thanks! Check your DMs.');
                setEnabled(existing.automation.enabled);
            } else {
                setSelectedPostId('');
                setTriggerWord('');
                setDmMessage('');
                setPublicReply('Thanks! Check your DMs.');
                setEnabled(true);
            }
        }
    }, [open, existing]);

    const isEdit = !!existing;
    const postId = isEdit ? existing!.social_post_id : selectedPostId;

    const handleSave = async () => {
        if (!postId) {
            showToastMessage('Select a post', 'error');
            return;
        }
        if (!triggerWord.trim()) {
            showToastMessage('Enter a trigger word', 'error');
            return;
        }
        if (!dmMessage.trim()) {
            showToastMessage('Enter a DM message', 'error');
            return;
        }
        try {
            await dispatchP(
                upsertBrandOpsAutomation({
                    brandId,
                    social_post_id: postId,
                    trigger_word: triggerWord.trim(),
                    dm_message: dmMessage.trim(),
                    public_reply_text: publicReply.trim(),
                    enabled,
                }),
            );
            showToastMessage(isEdit ? 'Automation updated' : 'Automation created', 'success');
            onClose();
        } catch (err: any) {
            showToastMessage(err?.message || 'Failed to save automation', 'error');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    color: '#fff',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                },
            }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    pb: 1,
                }}>
                {isEdit ? 'Edit Automation Rule' : 'New Automation Rule'}
                <IconButton onClick={onClose} sx={{ color: BRAND_LUXE_MUTED }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                {!isEdit && (
                    <Box>
                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 1 }}>
                            Select post (published Instagram posts)
                        </Typography>
                        {igPosts.length === 0 ? (
                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                                No published Instagram posts found. Publish a calendar item first.
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    maxHeight: 180,
                                    overflow: 'auto',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: 1.5,
                                }}>
                                {igPosts.map(p => (
                                    <Box
                                        key={p._id}
                                        onClick={() => setSelectedPostId(p._id)}
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            cursor: 'pointer',
                                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                                            bgcolor: selectedPostId === p._id ? 'rgba(255,201,101,0.1)' : 'transparent',
                                            '&:last-child': { borderBottom: 'none' },
                                        }}>
                                        <Typography
                                            sx={{
                                                color: selectedPostId === p._id ? BRAND_LUXE_PRIMARY : '#fff',
                                                fontSize: 13,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            {p.caption || p.title || p._id}
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                            {p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString() : ''}
                                            {' · '}
                                            {p.status}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {isEdit && existing && (
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                        Post:{' '}
                        <span style={{ color: '#fff' }}>
                            {existing.caption
                                ? existing.caption.slice(0, 60) + (existing.caption.length > 60 ? '…' : '')
                                : existing.social_post_id}
                        </span>
                    </Typography>
                )}

                <TextField
                    label="Trigger word(s)"
                    placeholder="e.g. link, info, DM"
                    helperText="Comments containing this word will trigger the automation. Comma-separate for multiple."
                    value={triggerWord}
                    onChange={e => setTriggerWord(e.target.value)}
                    fullWidth
                    sx={FIELD_SX}
                    FormHelperTextProps={{ sx: { color: BRAND_LUXE_MUTED } }}
                />

                <TextField
                    label="DM message"
                    placeholder="Hi! Here's the link you asked for…"
                    helperText="Sent as a private DM to commenters who match the trigger word."
                    value={dmMessage}
                    onChange={e => setDmMessage(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    sx={FIELD_SX}
                    FormHelperTextProps={{ sx: { color: BRAND_LUXE_MUTED } }}
                />

                <TextField
                    label="Public reply (optional)"
                    placeholder="Thanks! Check your DMs."
                    helperText="Visible reply posted on the comment."
                    value={publicReply}
                    onChange={e => setPublicReply(e.target.value)}
                    fullWidth
                    sx={FIELD_SX}
                    FormHelperTextProps={{ sx: { color: BRAND_LUXE_MUTED } }}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={enabled}
                            onChange={e => setEnabled(e.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    bgcolor: BRAND_LUXE_PRIMARY,
                                },
                            }}
                        />
                    }
                    label={
                        <Typography sx={{ color: '#fff', fontSize: 13 }}>{enabled ? 'Enabled' : 'Paused'}</Typography>
                    }
                />

                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', pt: 1 }}>
                    <ManageGoldButton
                        onClick={onClose}
                        sx={{
                            bgcolor: 'transparent',
                            color: BRAND_LUXE_MUTED,
                            border: '1px solid rgba(255,255,255,0.12)',
                        }}>
                        Cancel
                    </ManageGoldButton>
                    <ManageGoldButton onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create rule'}
                    </ManageGoldButton>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
